import React, { useState, useEffect } from 'react';
import { 
  X, Plus, Edit2, Trash2, Check, Settings, ChevronDown, ChevronUp,
  Car, Gavel, Home, DollarSign, FileText, Download,
  School, GraduationCap, Briefcase, Heart, Users, Building2,
  MapPin, Receipt, CreditCard, Lightbulb, Package, Leaf, Trash2 as TrashIcon
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


// Service icon mapping with colors
const getServiceIcon = (serviceName) => {
  const name = serviceName.toLowerCase();
  
  // Icon and color configurations
  const iconMap = {
    // Traffic & DMV
    'dmv': { Icon: Car, color: 'bg-blue-100 text-blue-600', label: 'DMV' },
    'vehicle': { Icon: Car, color: 'bg-blue-100 text-blue-600', label: 'Vehicle' },
    
    // Legal & Courts
    'ticket': { Icon: Gavel, color: 'bg-amber-100 text-amber-600', label: 'Tickets' },
    'citation': { Icon: Gavel, color: 'bg-amber-100 text-amber-600', label: 'Citations' },
    'court': { Icon: Gavel, color: 'bg-amber-100 text-amber-600', label: 'Court' },
    
    // Criminal Justice
    'bail': { Icon: Gavel, color: 'bg-red-100 text-red-600', label: 'Bail' },
    'probation': { Icon: FileText, color: 'bg-red-100 text-red-600', label: 'Probation' },
    
    // Parks & Recreation
    'recreation': { Icon: Leaf, color: 'bg-green-100 text-green-600', label: 'Recreation' },
    'park': { Icon: Leaf, color: 'bg-green-100 text-green-600', label: 'Parks' },
    
    // Waste Management
    'garbage': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600', label: 'Garbage' },
    'trash': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600', label: 'Trash' },
    'waste': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600', label: 'Waste' },
    'solid waste': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600', label: 'Solid Waste' },
    
    // Housing & Property
    'rent': { Icon: Home, color: 'bg-purple-100 text-purple-600', label: 'Rent' },
    'housing': { Icon: Home, color: 'bg-purple-100 text-purple-600', label: 'Housing' },
    'property': { Icon: Building2, color: 'bg-indigo-100 text-indigo-600', label: 'Property' },
    
    // Financial
    'tax': { Icon: Receipt, color: 'bg-rose-100 text-rose-600', label: 'Tax' },
    'payment': { Icon: CreditCard, color: 'bg-emerald-100 text-emerald-600', label: 'Payment' },
    
    // Permits & Licenses
    'permit': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600', label: 'Permits' },
    'license': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600', label: 'License' },
    'misc': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600', label: 'Misc' },
    
    // Education
    'school': { Icon: School, color: 'bg-yellow-100 text-yellow-600', label: 'School' },
    'education': { Icon: GraduationCap, color: 'bg-yellow-100 text-yellow-600', label: 'Education' },
    
    // Utilities
    'utilities': { Icon: Lightbulb, color: 'bg-orange-100 text-orange-600', label: 'Utilities' },
    'utility': { Icon: Lightbulb, color: 'bg-orange-100 text-orange-600', label: 'Utility' },
    
    // Community Services
    'child support': { Icon: Users, color: 'bg-pink-100 text-pink-600', label: 'Child Support' },
    'donation': { Icon: Heart, color: 'bg-red-100 text-red-500', label: 'Donation' },
    
    // eCommerce
    'estore': { Icon: Package, color: 'bg-violet-100 text-violet-600', label: 'eStore' },
    'store': { Icon: Package, color: 'bg-violet-100 text-violet-600', label: 'Store' },
  };
  
  // Find matching icon
  for (const [key, config] of Object.entries(iconMap)) {
    if (name.includes(key)) {
      return config;
    }
  }
  
  // Default icon for unmatched services
  return { Icon: DollarSign, color: 'bg-slate-100 text-slate-600', label: 'Service' };
};

export default function ServiceManagerEnhanced({ isOpen, onClose, onServicesUpdated }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const [editingCollections, setEditingCollections] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchServices();
    }
  }, [isOpen]);

  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      toast.info('Preparing CSV export...');
      const response = await api.get('/admin/services/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `GW_Services_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Services CSV downloaded');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/services');
      const servicesData = response.data.services;
      console.log('Fetched services:', servicesData.length);
      console.log('Sample service:', servicesData[0]);
      console.log('Has collection_amounts:', servicesData[0]?.collection_amounts ? 'YES' : 'NO');
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin or Approver role required.');
        onClose();
      } else {
        toast.error('Failed to fetch services');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.trim()) {
      toast.error('Service name cannot be empty');
      return;
    }

    try {
      await api.post('/admin/services', 
        { name: newService.trim(), active: true }
      );
      
      toast.success('Service added successfully');
      setNewService('');
      setShowAddForm(false);
      await fetchServices();
      onServicesUpdated();
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(error.response?.data?.detail || 'Failed to add service');
    }
  };

  const handleUpdateService = async (serviceId, newName) => {
    try {
      await api.put(`/admin/services/${serviceId}`,
        { name: newName, active: true }
      );
      
      toast.success('Service updated successfully');
      setEditingService(null);
      await fetchServices();
      onServicesUpdated();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleDeleteService = async (serviceId, serviceName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${serviceName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/services/${serviceId}`);
      
      toast.success(`${serviceName} deleted successfully`);
      await fetchServices();
      onServicesUpdated();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleToggleService = async (serviceId, serviceName, currentStatus) => {
    try {
      const response = await api.patch(`/admin/services/${serviceId}/toggle`, {}
      );
      
      const action = response.data.active ? 'activated' : 'deactivated';
      toast.success(`${serviceName} ${action} successfully`);
      await fetchServices();
      onServicesUpdated();
    } catch (error) {
      console.error('Error toggling service:', error);
      toast.error('Failed to toggle service status');
    }
  };

  const handleUpdateCollections = async (serviceId, collectionAmounts) => {
    try {
      const service = services.find(s => s.id === serviceId);
      
      await api.put(`/admin/services/${serviceId}`,
        { 
          name: service.name, 
          active: true,
          collection_amounts: collectionAmounts
        }
      );
      
      toast.success('Collection amounts updated successfully');
      setEditingCollections(null);
      await fetchServices();
    } catch (error) {
      console.error('Error updating collections:', error);
      toast.error('Failed to update collection amounts');
    }
  };

  const handleCollectionPercentChange = (rangeIndex, value) => {
    const newCollections = [...editingCollections.collection_amounts];
    newCollections[rangeIndex].percent = parseFloat(value) || 0;
    setEditingCollections({
      ...editingCollections,
      collection_amounts: newCollections
    });
  };

  const handleCollectionRangeChange = (rangeIndex, field, value) => {
    const newCollections = [...editingCollections.collection_amounts];
    const numValue = parseFloat(value) || 0;
    newCollections[rangeIndex][field] = numValue;
    
    // Auto-calculate midpoint when low or high changes
    if (field === 'low' || field === 'high') {
      const low = field === 'low' ? numValue : newCollections[rangeIndex].low;
      const high = field === 'high' ? numValue : newCollections[rangeIndex].high;
      
      // Calculate midpoint - if both low and high exist, calculate average; otherwise use what's available
      if (low !== null && low !== undefined && high !== null && high !== undefined) {
        newCollections[rangeIndex].midpoint = (low + high) / 2;
      } else if (high !== null && high !== undefined) {
        newCollections[rangeIndex].midpoint = high;
      } else if (low !== null && low !== undefined) {
        newCollections[rangeIndex].midpoint = low;
      }
    }
    
    // Auto-update range label
    const ca = newCollections[rangeIndex];
    if (ca.low === null || ca.low === undefined || ca.low === 0) {
      newCollections[rangeIndex].range = `≤ $${ca.high}`;
    } else if (ca.high === null || ca.high === undefined) {
      newCollections[rangeIndex].range = `$${ca.low}+`;
    } else {
      newCollections[rangeIndex].range = `$${ca.low}-$${ca.high}`;
    }
    
    setEditingCollections({
      ...editingCollections,
      collection_amounts: newCollections
    });
  };

  const getTotalPercent = (collectionAmounts) => {
    return collectionAmounts.reduce((sum, ca) => sum + (ca.percent || 0), 0).toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Manage Services</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              disabled={exporting || loading}
              data-testid="export-services-csv-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium text-sm disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Service Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="btn-primary w-full inline-flex items-center justify-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add New Service</span>
            </button>
          </div>

          {/* Add Service Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                New Service Name
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="Enter service name..."
                  className="input-field flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddService()}
                  autoFocus
                />
                <button
                  onClick={handleAddService}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="Save"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewService('');
                  }}
                  className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 transition-colors"
                  title="Cancel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Services List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-slate-600">Loading services...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  All Services ({services.length})
                </h3>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    <span>Active ({services.filter(s => s.active).length})</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span>Inactive ({services.filter(s => !s.active).length})</span>
                  </div>
                </div>
              </div>
              
              {services.map((service) => (
                <div
                  key={service.id}
                  className="border-2 border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Service Header */}
                  <div className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors">
                    {editingService?.id === service.id ? (
                      <div className="flex-1 flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingService.name}
                          onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                          className="input-field flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && handleUpdateService(service.id, editingService.name)}
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateService(service.id, editingService.name)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingService(null)}
                          className="px-3 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center space-x-3 flex-1">
                          {/* Service Icon Badge */}
                          {(() => {
                            const { Icon, color } = getServiceIcon(service.name);
                            return (
                              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}>
                                <Icon className="w-5 h-5" />
                              </div>
                            );
                          })()}
                          
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">{service.name}</span>
                            {service.collection_amounts && (
                              <span className="text-xs text-slate-500">
                                {service.collection_amounts.length} ranges · {getTotalPercent(service.collection_amounts)}% total
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              console.log('Chevron clicked for service:', service.name, 'Current expanded:', expandedService);
                              setExpandedService(expandedService === service.id ? null : service.id);
                              console.log('New expanded value:', expandedService === service.id ? null : service.id);
                            }}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View Collection Amounts"
                          >
                            {expandedService === service.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingService(service)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit Name"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          
                          {/* Toggle Switch */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleService(service.id, service.name, service.active);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              service.active ? 'bg-green-600' : 'bg-gray-300'
                            }`}
                            title={service.active ? 'Active - Click to Deactivate' : 'Inactive - Click to Activate'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                service.active ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          
                          {/* Delete Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteService(service.id, service.name);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Collection Amounts Editor */}
                  {(() => {
                    const isExpanded = expandedService === service.id;
                    const hasCollections = service.collection_amounts;
                    console.log(`Service ${service.name}: expanded=${isExpanded}, hasCollections=${hasCollections}, collectionCount=${hasCollections ? service.collection_amounts.length : 0}`);
                    return isExpanded && hasCollections ? (
                      <div className="p-4 bg-slate-50 border-t border-slate-200">
                        <div className="mb-3 flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-slate-700">Collection Amount Ranges</h4>
                        {editingCollections?.id === service.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleUpdateCollections(service.id, editingCollections.collection_amounts)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={() => setEditingCollections(null)}
                              className="px-3 py-1 text-sm bg-slate-300 text-slate-700 rounded hover:bg-slate-400 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingCollections({ id: service.id, collection_amounts: [...service.collection_amounts] })}
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                          >
                            Edit Ranges & Percentages
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts).map((ca, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border border-slate-200">
                            {editingCollections?.id === service.id ? (
                              // Edit Mode - Full Range Editing
                              <div className="space-y-3">
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">Low ($)</label>
                                    <input
                                      type="number"
                                      value={ca.low || ''}
                                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                      onChange={(e) => handleCollectionRangeChange(idx, 'low', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                                      step="1"
                                      min="0"
                                      placeholder="0"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">High ($)</label>
                                    <input
                                      type="number"
                                      value={ca.high || ''}
                                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                      onChange={(e) => handleCollectionRangeChange(idx, 'high', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                                      step="1"
                                      min="0"
                                      placeholder="∞"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium flex items-center">
                                      Midpoint ($)
                                      <span className="ml-1 text-indigo-600" title="Auto-calculated from Low and High">ⓘ</span>
                                    </label>
                                    <input
                                      type="number"
                                      value={ca.midpoint?.toFixed(2) || '0.00'}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-indigo-50 text-indigo-900 font-medium cursor-not-allowed"
                                      readOnly
                                      disabled
                                      title="Auto-calculated: (Low + High) / 2"
                                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">Percent (%)</label>
                                    <input
                                      type="number"
                                      value={ca.percent}
                                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                      onChange={(e) => handleCollectionPercentChange(idx, e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <div className="text-slate-500">
                                    Range: <span className="font-semibold text-slate-700">{ca.range}</span>
                                  </div>
                                  <div className="text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                                    Midpoint = (${ca.low || 0} + ${ca.high || 0}) / 2 = ${ca.midpoint?.toFixed(2) || '0.00'}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // View Mode
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-slate-700">{ca.range}</div>
                                  <div className="text-xs text-slate-500 mt-1">
                                    ${ca.low} - ${ca.high} (Midpoint: ${ca.midpoint})
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm font-semibold text-slate-900 w-16 text-right">
                                    {ca.percent?.toFixed(2)}%
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Total:</span>
                          <span className={`text-sm font-bold ${
                            Math.abs(getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts) - 100) < 0.1
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts)}%
                          </span>
                        </div>
                        {Math.abs(getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts) - 100) >= 0.1 && (
                          <p className="text-xs text-red-600 mt-1">⚠ Total should equal 100%</p>
                        )}
                      </div>
                    </div>
                  ) : null;
                  })()}
                </div>
              ))}

              {services.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No services found. Click "Add New Service" to create one.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="btn-secondary w-full"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
